import React from 'react';
import { Calendar, Button, Tag, Spin, Empty, Space, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useSlotPicker, AvailabilityResponse, OpenSlot, slotKey } from './useSlotPicker';
import { rolloverFinishLabel } from '../Public/bookingPortalHelpers';

const { Text } = Typography;

interface SlotPickerProps {
  title: string;
  fetchAvailability: (startDate: string, endDate: string) => Promise<AvailabilityResponse>;
  submit: (startTime: string, segments?: OpenSlot['segments']) => Promise<void>;
  confirmLabel?: string;
}

export const SlotPicker: React.FC<SlotPickerProps> = ({ title, fetchAvailability, submit, confirmLabel = 'Confirm' }) => {
  const p = useSlotPicker({ fetchAvailability, submit });

  const fmtDur = (m: number) => `${Math.floor(m / 60)}h ${m % 60}m`;

  const disabledDate = (d: Dayjs) => {
    if (d.isBefore(dayjs(), 'day')) return true;
    if (p.partsEta && d.isBefore(p.partsEta, 'day')) return true;
    return !p.openDays.has(d.format('YYYY-MM-DD'));
  };

  if (p.loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <Space direction="vertical" size={4} style={{ width: '100%', marginBottom: 12 }}>
        <Text strong>{title}</Text>
        {p.data && <Tag color="blue">~{fmtDur(p.data.duration_minutes)}</Tag>}
        {p.partsEta && <Text type="secondary">Parts arrive {p.partsEta.format('ddd MMM D')} — earliest after that.</Text>}
      </Space>

      <Calendar
        fullscreen={false}
        value={p.selectedDay || dayjs()}
        disabledDate={disabledDate}
        onSelect={(d) => { p.setSelectedDay(d); p.setSelectedSlot(null); }}
      />

      <div style={{ marginTop: 16 }}>
        <Text strong>{p.selectedDay ? p.selectedDay.format('ddd MMM D') : 'Pick a day'} — open times</Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {p.slotsForDay.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No open times" />}
          {p.slotsForDay.map((s) => {
            const key = slotKey(s);
            return (
              <Button
                key={key}
                size="large"
                type={p.selectedSlot === key ? 'primary' : 'default'}
                onClick={() => p.setSelectedSlot(key)}
              >
                {dayjs(s.start_time).format('h:mm A')}{s.is_multi_day ? ` → ${rolloverFinishLabel(s) || 'next day'}` : ''}
              </Button>
            );
          })}
        </div>
      </div>

      <Button
        type="primary" size="large" block style={{ marginTop: 20 }}
        disabled={!p.selectedSlotObj} loading={p.submitting} onClick={p.confirm}
      >
        {confirmLabel}{p.selectedSlotObj ? ` ${dayjs(p.selectedSlotObj.start_time).format('h:mm A')}${p.selectedSlotObj.is_multi_day ? ` → ${rolloverFinishLabel(p.selectedSlotObj) || 'next day'}` : ''}` : ''}
      </Button>
    </div>
  );
};
